import React, { useMemo } from 'react';
import type { TermTypeTotal } from '@/types';
import { FilterPill } from './FilterPillPanel';

/**
 * LR-700: quick Term filter in the summary bar — Open / Term / Evergreen with the
 * trade count of each in the current slice. A SECOND control over the sidebar's
 * "Trade Term" section, not a filter of its own: it reads and writes the same
 * `localTradesFilters.termTypes`, so a pick shows up in the sidebar section, in
 * "Applied filters: N" and in the column funnel — and saved views (LR-671) carry
 * it with no extra wiring, since they persist that same field.
 *
 * Counts come from the server aggregate (`term_type_totals`), scoped by
 * `buildTradesTermPillParams`: view ∩ sidebar filters ∩ search, minus only this
 * pill's own `term_types` predicate. The blotter is windowed (LR-546), so
 * counting the loaded page would under-report.
 *
 * Unlike CurrencyFilterPill this one renders in BOTH modes: the live MetricsBar,
 * and — since History has no metrics bar by design (`resolveTradesChrome`) — the
 * View-tab row on History → Trades.
 */

/** Stored value → the label the operator reads. Values the server may add that
 *  are missing here fall back to the raw code, so nothing is ever hidden. */
const TERM_LABELS: Record<string, string> = {
  open: 'Open',
  term: 'Term',
  evergreen: 'Evergreen',
};

const labelFor = (termType: string): string => TERM_LABELS[termType] ?? termType;

export interface TermFilterPillProps {
  /** Per-term-type counts, already in display order (Open → Term → Evergreen).
   *  Undefined while the aggregate loads → the pill lists nothing but stays
   *  clickable, matching the ccy pill's cold-load behaviour. */
  totals?: TermTypeTotal[];
  /** Selected term types — mirrors `localTradesFilters.termTypes`; [] = All. */
  selected: string[];
  onChange: (termTypes: string[]) => void;
  className?: string;
}

export const TermFilterPill: React.FC<TermFilterPillProps> = ({
  totals,
  selected,
  onChange,
  className,
}) => {
  const entries = useMemo(
    () =>
      (totals ?? []).map(({ term_type, count }) => ({
        key: term_type,
        label: labelFor(term_type),
        count,
      })),
    [totals],
  );

  // "All" / the single value's label / "2 of 3" — the ticket's wording. The
  // denominator is what the CURRENT slice offers (entries), not the three
  // possible values, so it can't claim "2 of 3" in a slice holding only two.
  const buttonLabel =
    selected.length === 0
      ? 'All'
      : selected.length === 1
        ? labelFor(selected[0])
        : `${selected.length} of ${Math.max(entries.length, selected.length)}`;

  return (
    <FilterPill
      className={className}
      caption="Term:"
      buttonLabel={buttonLabel}
      title="Filter by trade term — the same filter as the sidebar's Trade Term section"
      entries={entries}
      selected={selected}
      onChange={onChange}
      isolateTitle={(label, isolated) => (isolated ? 'Show all terms' : `Only ${label}`)}
      emptyMessage="No trade terms in view"
      panelClassName="w-56"
    />
  );
};
